import { useQuery, UseQueryResult, useQueryClient } from "@tanstack/react-query";
import { MimshakData } from "../interfaces/DTO/mimshakData";

/**
 * פרמטרים ליצירת Mimshak hooks
 */
interface MimshakHooksConfig<T extends MimshakData = MimshakData> {
  service: {
    getMimshaksDataByUserAndPeriod: (userId: number, period: string) => Promise<T[]>;
    getMimshaksDataByMultipleUsersAndPeriod?: (userIds: number[], period: string) => Promise<T[]>;
    getLatestRecordsByUser: (userId: number) => Promise<T[]>;
    getLatestByMultipleContacts: (userIds: number[]) => Promise<T[]>;
    upsertMimshakData?: (data: T) => Promise<T>;
  };
  queryKeys: {
    byPeriod: (userId: number, period: string) => any[];
    latestRecords: (userId: number) => any[];
    latestRecordsBatch: (userIds: string) => any[];
    byPeriodBatch: (userIds: string, period: string) => any[];
  };
}

/**
 * Factory function ליצירת כל ה-hooks עבור Mimshak
 */
export const createMimshakHooks = <T extends MimshakData = MimshakData>(config: MimshakHooksConfig<T>) => {
  const { service, queryKeys } = config;

  // ✅ שליפת נתוני Mimshak לפי contact בודד
  const useMimshaksByContactAndPeriod = (
    contactId: number,
    period?: string,
    enabled: boolean = true
  ): UseQueryResult<T[], Error> => {
    return useQuery<T[], Error>({
      queryKey: queryKeys.byPeriod(contactId, period || ""),
      queryFn: async () => {
        const result = await service.getMimshaksDataByUserAndPeriod(contactId, period!);
        return result;
      },
      enabled: enabled && !!contactId && !!period,
      staleTime: 1000 * 60 * 60 * 3, // 3 שעות - כמה זמן המידע "טרי" (לא שולח בקשה מיותרת)
      gcTime: 1000 * 60 * 2, // 2 דקות - כמה זמן לשמור בזיכרון אחרי שלא בשימוש (ניקוי תקופות ישנות)
      retry: 1,
    });
  };

  // ✅ שליפת נתוני Mimshak לפי מספר contacts - batch endpoint עם בדיקת React Query cache
  const useMimshaksByContactsAndPeriod = (
    contactIds: number[],
    period?: string,
    enabled: boolean = true
  ): UseQueryResult<T[], Error> => {
    const queryClient = useQueryClient();

    return useQuery<T[], Error>({
      queryKey: queryKeys.byPeriodBatch(contactIds.sort().join(","), period || ""),
      queryFn: async () => {
        // אם אין batch endpoint, נחזיר מערך ריק
        if (!service.getMimshaksDataByMultipleUsersAndPeriod) {
          return [] as T[];
        }

        // בדיקה אילו contacts כבר קיימים ב-React Query cache
        const missing: number[] = [];
        const cached: T[] = [];

        contactIds.forEach((contactId) => {
          const cacheKey = queryKeys.byPeriod(contactId, period!);
          const cachedResult = queryClient.getQueryData<T[]>(cacheKey);

          if (cachedResult) {
            // אם המידע קיים בקאש, נוסיף אותו לתוצאה
            cached.push(...cachedResult);
          } else {
            // אם לא קיים, נוסיף ל-missing
            missing.push(contactId);
          }
        });

        // אם כל המידע כבר בקאש, נחזיר אותו ישירות
        if (missing.length === 0) {
          console.log(`✅ All ${contactIds.length} contacts found in cache for period ${period}`);
          return cached;
        }

        // נשלח בקשה רק עבור contacts שחסרים
        console.log(`📡 Fetching ${missing.length} of ${contactIds.length} contacts from server for period ${period}`);
        const newData = await service.getMimshaksDataByMultipleUsersAndPeriod(missing, period!);

        // נעדכן את React Query cache לכל contact בנפרד
        missing.forEach((contactId) => {
          const contactSpecificData = newData.filter(item => item.contact_id === contactId);
          queryClient.setQueryData(queryKeys.byPeriod(contactId, period!), contactSpecificData);
        });

        // נחזיר את המידע המשולב (cached + חדש מהשרת)
        return [...cached, ...newData];
      },
      enabled: enabled && contactIds.length > 0 && !!period,
      staleTime: 1000 * 60 * 60 * 3, // 3 hours - המידע נחשב "טרי"
      gcTime: 1000 * 60 * 2, // 2 דקות - ניקוי מהיר של תקופות ישנות שלא בשימוש
      retry: 1,
    });
  };

  // ✅ שליפת הנתונים האחרונים – קריאה בודדת לפי contact
  const useLatestMimshakByContact = (
    contactId: number,
    enabled: boolean = true
  ): UseQueryResult<T[], Error> => {
    return useQuery<T[], Error>({
      queryKey: queryKeys.latestRecords(contactId),
      queryFn: async () => {
        const result = await service.getLatestRecordsByUser(contactId);
        return result;
      },
      enabled: !!contactId && enabled,
      staleTime: 1000 * 60 * 10, // 10 minutes
      retry: 1,
    });
  };

  // ✅ שליפת הנתונים האחרונים לכל contacts - batch endpoint עם בדיקת React Query cache
  const useLatestMimshaksByContacts = (
    contactIds: number[],
    enabled: boolean = true
  ): UseQueryResult<T[], Error> => {
    const queryClient = useQueryClient();

    return useQuery<T[], Error>({
      queryKey: queryKeys.latestRecordsBatch(contactIds.sort().join(",")),
      queryFn: async () => {
        // בדיקה אילו contacts כבר קיימים ב-React Query cache
        const missing: number[] = [];
        const cached: T[] = [];

        contactIds.forEach((contactId) => {
          const cacheKey = queryKeys.latestRecords(contactId);
          const cachedResult = queryClient.getQueryData<T[]>(cacheKey);

          if (cachedResult) {
            // אם המידע קיים בקאש, נוסיף אותו לתוצאה
            cached.push(...cachedResult);
          } else {
            // אם לא קיים, נוסיף ל-missing
            missing.push(contactId);
          }
        });

        // אם כל המידע כבר בקאש, נחזיר אותו ישירות
        if (missing.length === 0) {
          console.log(`✅ All ${contactIds.length} contacts found in cache (latest records)`);
          return cached;
        }

        // נשלח בקשה רק עבור contacts שחסרים
        console.log(`📡 Fetching latest records for ${missing.length} of ${contactIds.length} contacts from server`);
        const newData = await service.getLatestByMultipleContacts(missing);

        // נעדכן את React Query cache לכל contact בנפרד
        missing.forEach((contactId) => {
          const contactData = newData.filter(item => item.contact_id === contactId);
          queryClient.setQueryData(queryKeys.latestRecords(contactId), contactData);
        });

        // נחזיר את המידע המשולב (cached + חדש מהשרת)
        return [...cached, ...newData];
      },
      enabled: enabled && contactIds.length > 0,
      staleTime: 1000 * 60 * 10, // ✅ שמירה במטמון למשך 10 דקות
      gcTime: 1000 * 60 * 2, // 2 דקות - ניקוי מהיר
      retry: 1,
    });
  };

  return {
    useMimshaksByContactAndPeriod,
    useMimshaksByContactsAndPeriod,
    useLatestMimshakByContact,
    useLatestMimshaksByContacts,
  };
};
